import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import { PageHeader } from "../components/layout/PageHeader";
import { Card } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { fetchTicketById, updateTicket, addTicketReply } from "../store/actions/supportActions";
import { ArrowLeft, Send } from "lucide-react";

const ReplyBlock = styled.div`
  padding: 16px;
  margin-bottom: 12px;
  border-radius: 8px;
  background: ${({ $staff }) => ($staff ? "rgba(155, 135, 245, 0.08)" : "rgba(0,0,0,0.02)")};
  border-left: 4px solid ${({ $staff }) => ($staff ? "#6366f1" : "#e5e7eb")};
`;

const ReplyMeta = styled.div`
  font-size: 12px;
  color: #6b7280;
  margin-bottom: 8px;
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 80px;
  padding: 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
`;

export default function TicketDetail() {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { ticket, loading, error } = useSelector((state) => state.support);
  const [replyMessage, setReplyMessage] = useState("");

  useEffect(() => {
    if (ticketId) dispatch(fetchTicketById(ticketId));
  }, [dispatch, ticketId]);

  const handleStatusChange = (e) => {
    const status = e.target.value;
    if (ticket?._id && status) dispatch(updateTicket(ticket._id, { status }));
  };

  const handleReply = (e) => {
    e.preventDefault();
    if (!replyMessage.trim() || !ticket?._id) return;
    dispatch(addTicketReply(ticket._id, replyMessage.trim()));
    setReplyMessage("");
  };

  const replies = ticket?.replies || [];
  const statusOptions = ["open", "in-progress", "resolved", "closed"];

  return (
    <div className="animate-fadeIn">
      <PageHeader
        title="Support ticket"
        sub={ticket ? ticket.subject : "Loading..."}
      />
      <Button $variant="ghost" $size="sm" style={{ marginBottom: 16 }} onClick={() => navigate("/support")}>
        <ArrowLeft size={16} style={{ marginRight: 6 }} />
        Back to Support
      </Button>
      {loading && !ticket && <Card style={{ padding: 24 }}><p style={{ color: "#6b7280" }}>Loading...</p></Card>}
      {error && <Card style={{ padding: 24 }}><p style={{ color: "#dc2626" }}>{error}</p></Card>}
      {ticket && (
        <Card style={{ padding: 24 }}>
          <div style={{ marginBottom: 16 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: "#6b7280", textTransform: "uppercase" }}>Subject</span>
            <p style={{ margin: "4px 0 0", fontSize: 18, fontWeight: 600 }}>{ticket.subject}</p>
          </div>
          <div style={{ marginBottom: 16 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: "#6b7280", textTransform: "uppercase" }}>Message</span>
            <p style={{ margin: "4px 0 0", fontSize: 14, lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{ticket.message}</p>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 16, alignItems: "center" }}>
            <div>
              <span style={{ fontSize: 12, fontWeight: 600, color: "#6b7280", textTransform: "uppercase" }}>Status</span>
              <div style={{ marginTop: 4 }}>
                <select
                  value={ticket.status || "open"}
                  onChange={handleStatusChange}
                  disabled={loading}
                  style={{ padding: "8px 12px", borderRadius: 6, border: "1px solid #d1d5db", minWidth: 140 }}
                >
                  {statusOptions.map((s) => (
                    <option key={s} value={s}>{s.replace("-", " ")}</option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <span style={{ fontSize: 12, fontWeight: 600, color: "#6b7280", textTransform: "uppercase" }}>Priority</span>
              <p style={{ margin: "4px 0 0", fontSize: 14 }}>{ticket.priority || "medium"}</p>
            </div>
            <div>
              <span style={{ fontSize: 12, fontWeight: 600, color: "#6b7280", textTransform: "uppercase" }}>Created</span>
              <p style={{ margin: "4px 0 0", fontSize: 14 }}>{ticket.createdAt ? new Date(ticket.createdAt).toLocaleString() : "—"}</p>
            </div>
          </div>

          {replies.length > 0 && (
            <div style={{ marginTop: 24 }}>
              <h3 style={{ margin: "0 0 16px 0", fontSize: 16 }}>Replies</h3>
              {replies.map((r, i) => (
                <ReplyBlock key={i} $staff={r.isStaff}>
                  <ReplyMeta>
                    {r.isStaff ? "Support" : "You"} · {r.createdAt ? new Date(r.createdAt).toLocaleString() : ""}
                  </ReplyMeta>
                  <p style={{ margin: 0, fontSize: 14, lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{r.message}</p>
                </ReplyBlock>
              ))}
            </div>
          )}

          {(ticket.status === "open" || ticket.status === "in-progress") && (
            <div style={{ marginTop: 24 }}>
              <h3 style={{ margin: "0 0 16px 0", fontSize: 16 }}>Add your reply</h3>
              <form onSubmit={handleReply}>
                <TextArea
                  value={replyMessage}
                  onChange={(e) => setReplyMessage(e.target.value)}
                  placeholder="Type your reply…"
                  disabled={loading}
                />
                <Button type="submit" disabled={loading || !replyMessage.trim()} style={{ marginTop: 12 }}>
                  <Send size={16} />
                  Send reply
                </Button>
              </form>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
